import { useId } from "react";

import "./RulesGuide.css";

export type RulesGuideProps = Readonly<{
  onClose: () => void;
}>;

export function RulesGuide({ onClose }: RulesGuideProps) {
  const titleId = useId();

  return (
    <article className="rules-guide" aria-labelledby={titleId}>
      <header className="rules-guide__header">
        <div>
          <p className="rules-guide__eyebrow">How to play</p>
          <h2 id={titleId}>The rules of Passo</h2>
        </div>
        <button type="button" onClick={onClose} aria-label="Close rules">
          <span aria-hidden="true">×</span>
        </button>
      </header>

      <p className="rules-guide__lead">
        Cross the opponent’s farthest disc before the board falls apart around you.
      </p>

      <ol className="rules-guide__steps">
        <li>
          <span className="rules-guide__number">1</span>
          <div>
            <h3>Move a top disc</h3>
            <p>
              Black starts. Move one of your topmost discs to any adjacent field—straight or
              diagonal. You cannot move a disc buried in a stack.
            </p>
          </div>
        </li>
        <li>
          <span className="rules-guide__number">2</span>
          <div>
            <h3>Build stacks of three</h3>
            <p>
              Land on empty fields or either color. Stacks keep their bottom-to-top order and can
              hold at most three discs. Only the top disc controls the next move.
            </p>
          </div>
        </li>
        <li>
          <span className="rules-guide__number">3</span>
          <div>
            <h3>Leave no path behind</h3>
            <p>
              When the last disc leaves a field, that field disappears permanently. A field with
              none of its eight neighbors remaining is orphaned; it and its complete stack also
              leave the game.
            </p>
          </div>
        </li>
        <li>
          <span className="rules-guide__number">4</span>
          <div>
            <h3>Pass the frontier</h3>
            <p>
              Win by moving straight or diagonally forward beyond the opposing disc farthest from
              your home row at the start of the move. A blocking disc still counts when moving will
              later make its field disappear. Covered discs count too. A passage leaving the board
              must be straight ahead. A passage exits instead of landing even when an in-board
              target field is missing or full.
            </p>
          </div>
        </li>
      </ol>

      <section className="rules-guide__outcomes" aria-labelledby={`${titleId}-outcomes`}>
        <h3 id={`${titleId}-outcomes`}>How a match ends</h3>
        <ul>
          <li>If your turn begins without a legal move, you lose. Turns cannot be skipped.</li>
          <li>
            The match is a draw when the exact same position appears for the third time. That means
            the same fields, the same bottom-to-top order in every stack, and the same player to
            move. The appearances do not need to be consecutive, and the starting position counts.
          </li>
          <li>
            Passage wins and no-move losses are settled before repetition. There is no separate move
            limit.
          </li>
        </ul>
      </section>

      <div className="rules-guide__stack-key" aria-label="Stack order example">
        <div className="rules-guide__mini-stack" aria-hidden="true">
          <span className="rules-guide__mini-disc rules-guide__mini-disc--red" />
          <span className="rules-guide__mini-disc rules-guide__mini-disc--black" />
        </div>
        <p>
          <strong>Read stacks from bottom to top.</strong>
          <span>Here, red is buried and black may move.</span>
        </p>
      </div>
    </article>
  );
}
